import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import editIcon from "/icons/edit.png";
import deleteIcon from "/icons/delete.png";
import pawIcon from "/icons/paw.png";
import DeleteConfirmationModal from "../modals/DeleteConfirmation";
import { useAuth } from "../../hooks/useAuth";
import api from "../../services/api";
import "./BehavioralDataView.css";

const BehavioralDataView = ({ petData }) => {
  const { t, i18n } = useTranslation();
  const { getUserType } = useAuth();
  const isRTL = i18n.language === "ar";
  
  // Get user type color
  const currentUserType = getUserType();
  const getUserTypeColor = (userType) => {
    const userTypeConfig = {
      'PET_GUARDIAN': '#8548A8', // Purple
      'VETERINARIAN': '#00D4B3', // Cyan
      'SERVICE_PROVIDER': '#D65745', // Orange
      'SERVICE_PROVIDER': '#D65745', // Orange
      'CLINIC_MODERATOR': '#2A8AD2', // Blue
    };
    return userTypeConfig[userType] || '#8548A8'; // Default to purple
  };
  const userTypeColor = getUserTypeColor(currentUserType);
  const getId = (row) => row?.id ?? row?.ID ?? row?._id ?? null;
  const normalizeTraining = (row) => ({
    id: getId(row),
    Trainer: row.Trainer ?? row.trainer ?? "",
    "Training Type": row["Training Type"] ?? row.training_type ?? "",
    Progress: row.Progress ?? row.progress ?? "",
  });
  const normalizeIssue = (row) => ({
    id: getId(row),
    Issue: row.Issue ?? row.issue ?? "",
    Severity: row.Severity ?? row.severity ?? "",
    "Management Plan": row["Management Plan"] ?? row.management_plan ?? "",
  });

  const petLabel = petData
    ? `${petData.species} / ${petData.breed} – ${petData.age}`
    : "";

  const rowsPerPage = 2;

  const cap = (s) =>
    typeof s === "string" && s ? s[0].toUpperCase() + s.slice(1) : s;
  const toEnum = (s) => (s ? String(s).toLowerCase() : "");

  const [traits, setTraits] = useState({
    id: null,
    temperament: "",
    socialization: "",
  });
  const [traitsFormOpen, setTraitsFormOpen] = useState(false);
  const [traitsFormData, setTraitsFormData] = useState({
    temperament: [], // Changed to array for multiple selections
    socialization: [], // Changed to array for multiple selections
  });
  // Store original form data to detect changes
  const [originalTraitsFormData, setOriginalTraitsFormData] = useState({
    temperament: [],
    socialization: [],
  });
  
  // Store valid options from backend
  const [validTemperaments, setValidTemperaments] = useState([
    "friendly", "playful", "calm", "energetic", "shy", "confident",
    "curious", "independent", "affectionate", "protective", "gentle",
    "bold", "reserved", "outgoing", "cautious", "adventurous",
    "loyal", "intelligent", "stubborn", "easy-going", "sensitive",
    "dominant", "submissive", "alert", "relaxed", "fearful"
  ]);
  const [validSocializations, setValidSocializations] = useState([
    "mild", "moderate", "severe", "excellent", "good", "needs-improvement"
  ]);

  // Training Records state
  const [trainingData, setTrainingData] = useState([]);
  const [trainingPage, setTrainingPage] = useState(1);
  const [trainingFormOpen, setTrainingFormOpen] = useState(false);
  const [trainingFormMode, setTrainingFormMode] = useState("add");
  const [trainingFormData, setTrainingFormData] = useState({});

  // Issues state
  const [issuesData, setIssuesData] = useState([]);
  const [issuesPage, setIssuesPage] = useState(1);
  const [issuesFormOpen, setIssuesFormOpen] = useState(false);
  const [issuesFormMode, setIssuesFormMode] = useState("add");
  const [issuesFormData, setIssuesFormData] = useState({});

  // Delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteType, setDeleteType] = useState(null);

  // Fetch valid options from backend on component mount
  useEffect(() => {
    const fetchValidOptions = async () => {
      try {
        // Fetch valid temperaments - preserve original case from backend
        const tempRes = await api.get(`/api/v1/pet-health/behavioral-traits/temperaments`);
        // API returns: { data: { temperaments: [...] } }
        if (tempRes?.data?.temperaments && Array.isArray(tempRes.data.temperaments)) {
          const temps = tempRes.data.temperaments;
          const formattedTemps = temps.map(t => String(t).trim());
          setValidTemperaments(formattedTemps);
        } else if (Array.isArray(tempRes?.data)) {
          // Fallback: if data is directly an array
          const formattedTemps = tempRes.data.map(t => String(t).trim());
          setValidTemperaments(formattedTemps);
        }
        
        // Fetch valid socializations - preserve original case from backend
        const socRes = await api.get(`/api/v1/pet-health/behavioral-traits/socializations`);
        // API returns: { data: { socializations: [...] } } or similar
        if (socRes?.data?.socializations && Array.isArray(socRes.data.socializations)) {
          const socs = socRes.data.socializations;
          const formattedSocs = socs.map(s => String(s).trim());
          setValidSocializations(formattedSocs);
        } else if (Array.isArray(socRes?.data)) {
          // Fallback: if data is directly an array
          const formattedSocs = socRes.data.map(s => String(s).trim());
          setValidSocializations(formattedSocs);
        } else if (socRes?.data?.data && Array.isArray(socRes.data.data)) {
          // Another fallback format
          const formattedSocs = socRes.data.data.map(s => String(s).trim());
          setValidSocializations(formattedSocs);
        }
      } catch (e) {
        console.error("Failed to fetch valid options, using defaults", e);
        // Keep default values if API fails
      }
    };
    
    fetchValidOptions();
  }, []);

  useEffect(() => {
    if (!petData?.id) return;
    // Wait a bit for valid options to be loaded (they load on mount)
    // This ensures validTemperaments and validSocializations are available

    const loadTraits = async () => {
      try {
        const res = await api.get(`/api/v1/pet-health/behavioral-traits`, {
          params: { pet_id: petData.id },
        });
        const json = res.data;

        const row = Array.isArray(json?.data)
          ? json.data[0]
          : json?.data ?? json;

        if (row) {
          // Backend now returns arrays directly (JSON columns)
          // Match values to valid options to preserve exact case
          const parseTemperament = Array.isArray(row.temperament)
            ? row.temperament.map(t => {
                const val = String(t).trim();
                // Try to find exact match in valid options first
                const exactMatch = validTemperaments.find(vt => vt === val);
                if (exactMatch) return exactMatch;
                // Then try case-insensitive match
                const lowerMatch = validTemperaments.find(vt => vt.toLowerCase() === val.toLowerCase());
                return lowerMatch || val.toLowerCase();
              }).filter(Boolean)
            : (row.temperament && typeof row.temperament === 'string' 
              ? row.temperament.split(',').map(t => {
                  const val = t.trim();
                  const lowerMatch = validTemperaments.find(vt => vt.toLowerCase() === val.toLowerCase());
                  return lowerMatch || val.toLowerCase();
                }).filter(Boolean)
              : []);
          
          const parseSocialization = Array.isArray(row.socialization)
            ? row.socialization.map(s => {
                const val = String(s).trim();
                // Try to find exact match in valid options first
                const exactMatch = validSocializations.find(vs => vs === val);
                if (exactMatch) return exactMatch;
                // Then try case-insensitive match
                const lowerMatch = validSocializations.find(vs => vs.toLowerCase() === val.toLowerCase());
                return lowerMatch || val.toLowerCase();
              }).filter(Boolean)
            : (row.socialization && typeof row.socialization === 'string'
              ? row.socialization.split(',').map(s => {
                  const val = s.trim();
                  const lowerMatch = validSocializations.find(vs => vs.toLowerCase() === val.toLowerCase());
                  return lowerMatch || val.toLowerCase();
                }).filter(Boolean)
              : []);
          
          setTraits({
            id: row.id ?? null,
            temperament: Array.isArray(row.temperament) ? row.temperament.join(', ') : (row.temperament || ""),
            socialization: Array.isArray(row.socialization) ? row.socialization.join(', ') : (row.socialization || ""),
          });
          setTraitsFormData({
            temperament: parseTemperament,
            socialization: parseSocialization,
          });
        } else {
          setTraits({ id: null, temperament: "", socialization: "" });
          setTraitsFormData({ temperament: [], socialization: [] });
          setOriginalTraitsFormData({ temperament: [], socialization: [] });
        }
      } catch (e) {
        console.error("Failed to load traits", e);
      }
    };

    const loadTraining = async () => {
      try {
        const res = await api.get(`/api/v1/pet-health/training-records`, {
          params: { pet_id: petData.id },
        });
        const json = res.data;
        const rows = (json?.data ?? json ?? []).map(normalizeTraining);
        setTrainingData(rows);
      } catch (e) {
        console.error("Failed to load training records", e);
      }
    };
    const loadIssues = async () => {
      try {
        const { data: json } = await api.get(`/api/v1/pet-health/issues`, {
          params: { pet_id: petData.id },
        });
        const rows = (json?.data ?? json ?? []).map(normalizeIssue);
        setIssuesData(rows);
      } catch (e) {
        console.error("Failed to load issues", e);
      }
    };

    loadTraits();
    loadTraining();
    loadIssues();
  }, [petData?.id]);

  const deleteTraits = async () => {
    if (!traits.id) return;
    try {
      await api.delete(`/api/v1/pet-health/behavioral-traits-id/${traits.id}`);
      setTraits({ id: null, temperament: "", socialization: "" });
      setTraitsFormData({ temperament: [], socialization: [] });
      setOriginalTraitsFormData({ temperament: [], socialization: [] });
      setTraitsFormOpen(false);
    } catch (e) {
      console.error("Failed to delete traits", e);
    }
  };
  const deleteTrainingAtIndex = async (idx) => {
    const row = trainingData[idx];
    if (!row?.id) return;
    try {
      const res = await api.delete(
        `/api/v1/pet-health/training-records-id/${row.id}`
      );
      setTrainingData((prev) => prev.filter((_, i) => i !== idx));
    } catch (e) {
      console.error("Failed to delete training record", e);
    }
  };
  const deleteIssueAtIndex = async (idx) => {
    const row = issuesData[idx];
    if (!row?.id) return;
    try {
      const res = await api.delete(`/api/v1/pet-health/issues-id/${row.id}`);
      setIssuesData((prev) => prev.filter((_, i) => i !== idx));
    } catch (e) {
      console.error("Failed to delete issue", e);
    }
  };
  // Handlers
  const handleAddTraining = () => {
    setTrainingFormMode("add");
    setTrainingFormData({ Trainer: "", "Training Type": "", Progress: "" });
    setTrainingFormOpen(true);
  };
  const handleEditTraining = (row, index) => {
    setTrainingFormMode("edit");
    setTrainingFormData({ ...row, _index: index, id: row.id }); // keep id for PUT
    setTrainingFormOpen(true);
  };

  const handleAddIssue = () => {
    setIssuesFormMode("add");
    setIssuesFormData({ Issue: "", Severity: "", "Management Plan": "" });
    setIssuesFormOpen(true);
  };
  const handleEditIssue = (row, index) => {
    setIssuesFormMode("edit");
    setIssuesFormData({ ...row, _index: index, id: row.id }); // keep id for PUT
    setIssuesFormOpen(true);
  };

  const saveTraining = async () => {
    if (!petData?.id) return;

    // UI → API payload
    const payload = {
      pet_id: petData.id,
      trainer: trainingFormData.Trainer ?? trainingFormData.trainer ?? "",
      training_type:
        trainingFormData["Training Type"] ??
        trainingFormData.training_type ??
        "",
      progress: trainingFormData.Progress ?? trainingFormData.progress ?? "",
    };

    const isEdit = Boolean(trainingFormData.id);
    const url = isEdit
      ? `/api/v1/pet-health/training-records/${trainingFormData.id}`
      : `/api/v1/pet-health/training-records`;
    const method = isEdit ? "PUT" : "POST";

    try {
      const { data: saved } = await api({ url, method, data: payload });
      const normalized = normalizeTraining(saved?.data ?? saved);
      setTrainingData((prev) =>
        isEdit
          ? prev.map((r, i) => (i === trainingFormData._index ? normalized : r))
          : [...prev, normalized]
      );
      // optional: jump to last page when adding
      if (!isEdit) {
        const nextLength = trainingData.length + 1;
        const rowsPerPage = 2; // keep in sync with your const
        setTrainingPage(Math.ceil(nextLength / rowsPerPage));
      }

      setTrainingFormOpen(false);
      setTrainingFormData({});
    } catch (e) {
      console.error("Error saving training", e);
    }
  };

  const saveIssue = async () => {
    if (!petData?.id) return;
    const payload = {
      pet_id: petData.id,
      issue: issuesFormData.Issue ?? issuesFormData.issue ?? "",
      severity: (
        issuesFormData.Severity ??
        issuesFormData.severity ??
        ""
      ).toLowerCase(),
      management_plan:
        issuesFormData["Management Plan"] ??
        issuesFormData.management_plan ??
        "",
    };

    const isEdit = Boolean(issuesFormData.id);
    const url = isEdit
      ? `/api/v1/pet-health/issues/${issuesFormData.id}`
      : `/api/v1/pet-health/issues`;
    const method = isEdit ? "PUT" : "POST";

    try {
      const { data: saved } = await api({ url, method, data: payload });
      const normalized = normalizeIssue(saved?.data ?? saved);

      setIssuesData((prev) => {
        if (isEdit) {
          return prev.map((r, i) =>
            i === issuesFormData._index ? normalized : r
          );
        }
        return [...prev, normalized];
      });

      setIssuesFormOpen(false);
      setIssuesFormData({});
    } catch (e) {
      console.error("Error saving issue", e);
    }
  };

  const saveTraits = async () => {
    if (!petData?.id) return;

    // Backend now expects arrays directly (JSON columns)
    // Use the exact format from backend (preserve original case from valid options)
    const temperamentArray = Array.isArray(traitsFormData.temperament)
      ? traitsFormData.temperament
          .map(t => {
            // Find matching valid option to get exact format (case-insensitive match, return exact case)
            const lowerT = String(t).toLowerCase().trim();
            const matched = validTemperaments.find(vt => vt.toLowerCase().trim() === lowerT);
            if (matched) {
              return matched; // Return exact case from valid options
            }
            console.warn(`Temperament "${t}" not found in valid options:`, validTemperaments);
            // If not found, try to capitalize properly as fallback
            const fallback = String(t).split('-').map(word => 
              word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            ).join('-');
            return fallback;
          })
          .filter(Boolean)
      : [];

    const socializationArray = Array.isArray(traitsFormData.socialization)
      ? traitsFormData.socialization
          .map(s => {
            // Find matching valid option to get exact format (case-insensitive match, return exact case)
            const lowerS = String(s).toLowerCase().trim();
            const matched = validSocializations.find(vs => vs.toLowerCase().trim() === lowerS);
            if (matched) {
              return matched; // Return exact case from valid options
            }
            console.warn(`Socialization "${s}" not found in valid options:`, validSocializations);
            // If not found, try to capitalize properly as fallback
            const fallback = String(s).split('-').map(word => 
              word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            ).join('-');
            return fallback;
          })
          .filter(Boolean)
      : [];


    // Validation: At least one item required in each array
    if (temperamentArray.length === 0 || socializationArray.length === 0) {
      alert(t("behavioralData.validation.atLeastOneRequired") || "Please select at least one option for both temperament and socialization.");
      return;
    }

    const payload = {
      pet_id: petData.id,
      temperament: temperamentArray, // Send as array
      socialization: socializationArray, // Send as array
    };

    const isEdit = Boolean(traits.id);
    const url = isEdit
      ? `/api/v1/pet-health/behavioral-traits/${traits.id}`
      : `/api/v1/pet-health/behavioral-traits`;

    const method = isEdit ? "PUT" : "POST";

    try {
      const { data: saved } = await api({ url, method, data: payload });
      const obj = saved?.data ?? saved;

      // Backend returns arrays directly (JSON columns)
      // Handle both array format (new) and string format (backward compatibility)
      const parseTemperament = Array.isArray(obj.temperament)
        ? obj.temperament.map(t => String(t).trim().toLowerCase()).filter(Boolean)
        : (obj.temperament && typeof obj.temperament === 'string'
          ? obj.temperament.split(',').map(t => t.trim().toLowerCase()).filter(Boolean)
          : []);
      
      const parseSocialization = Array.isArray(obj.socialization)
        ? obj.socialization.map(s => String(s).trim().toLowerCase()).filter(Boolean)
        : (obj.socialization && typeof obj.socialization === 'string'
          ? obj.socialization.split(',').map(s => s.trim().toLowerCase()).filter(Boolean)
          : []);

      // sync UI - format for display
      const displayTemperament = Array.isArray(obj.temperament) 
        ? obj.temperament.join(', ')
        : (obj.temperament || "");
      const displaySocialization = Array.isArray(obj.socialization)
        ? obj.socialization.join(', ')
        : (obj.socialization || "");

      setTraits({
        id: obj.id ?? traits.id, // keep id on POST/PUT
        temperament: displayTemperament, // read-only display
        socialization: displaySocialization,
      });
      setTraitsFormData({
        temperament: parseTemperament,
        socialization: parseSocialization,
      });
      // Update original data after successful save
      setOriginalTraitsFormData({
        temperament: [...parseTemperament],
        socialization: [...parseSocialization],
      });
      setTraitsFormOpen(false);
    } catch (e) {
      console.error("Error saving traits", e);
    }
  };

  const handleDelete = (type, index) => {
    setDeleteTarget(index);
    setDeleteType(type);
    setShowDeleteModal(true);
  };
  const confirmDelete = () => {
    if (deleteType === "training") {
      deleteTrainingAtIndex(deleteTarget);
    } else if (deleteType === "issues") {
      deleteIssueAtIndex(deleteTarget);
    }
    setShowDeleteModal(false);
  };

  const cancelDelete = () => setShowDeleteModal(false);

  const currentTraining = trainingData.slice(
    (trainingPage - 1) * rowsPerPage,
    trainingPage * rowsPerPage
  );
  const currentIssues = issuesData.slice(
    (issuesPage - 1) * rowsPerPage,
    issuesPage * rowsPerPage
  );

  return (
    <div className="behavioral-data-container" style={{ marginBottom: "40px", "--user-type-color": userTypeColor }} dir={isRTL ? "rtl" : "ltr"}>
      {/* Behavioral Traits */}
      <div className="behavioral-section">
        <div className="behavioral-section-header">
          <h2 className="behavioral-section-title">
            <img src={pawIcon} alt="icon" />
            {t("behavioralData.behavioralTraits")}
          </h2>
          {!traitsFormOpen && (
            <button
              className="behavioral-action-btn"
              onClick={() => {
                // Parse existing traits into arrays
                const parseTemperament = traits.temperament 
                  ? (typeof traits.temperament === 'string' 
                    ? traits.temperament.split(',').map(t => t.trim().toLowerCase()).filter(Boolean)
                    : (Array.isArray(traits.temperament) 
                      ? traits.temperament.map(t => String(t).trim().toLowerCase()).filter(Boolean)
                      : []))
                  : [];
                
                const parseSocialization = traits.socialization
                  ? (typeof traits.socialization === 'string'
                    ? traits.socialization.split(',').map(s => s.trim().toLowerCase()).filter(Boolean)
                    : (Array.isArray(traits.socialization)
                      ? traits.socialization.map(s => String(s).trim().toLowerCase()).filter(Boolean)
                      : []))
                  : [];
                
                setTraitsFormData({
                  temperament: parseTemperament,
                  socialization: parseSocialization,
                });
                setOriginalTraitsFormData({
                  temperament: traits.id ? [...parseTemperament] : [],
                  socialization: traits.id ? [...parseSocialization] : [],
                });
                setTraitsFormOpen(true);
              }}
            >
              {t("behavioralData.editTraits")}
            </button>
          )}
        </div>

        {traitsFormOpen ? (
          <div className="behavioral-form-card">
            <div className="behavioral-form-header">
              <h2>
                {traits.id
                  ? t("behavioralData.editBehavioralTraits")
                  : t("behavioralData.addBehavioralTraits")}
              </h2>
            </div>
            <div className="behavioral-form-content">
              <div className="behavioral-form-field">
                <label className="behavioral-form-label">
                  {t("behavioralData.temperament")}
                </label>
                <div className="behavioral-pill-container">
                {validTemperaments.map((option) => {
                  const isChecked = traitsFormData.temperament?.some(t => 
                    t.toLowerCase().trim() === option.toLowerCase().trim()
                  ) || false;
                  return (
                    <button
                      key={option}
                      type="button"
                      className={`behavioral-pill-btn ${isChecked ? 'active' : ''}`}
                      onClick={() => {
                        const current = traitsFormData.temperament || [];
                        const lowerOption = option.toLowerCase().trim();
                        if (!isChecked) {
                          setTraitsFormData({
                            ...traitsFormData,
                            temperament: [...current.filter(t => 
                              t.toLowerCase().trim() !== lowerOption
                            ), option],
                          });
                        } else {
                          setTraitsFormData({
                            ...traitsFormData,
                            temperament: current.filter((t) => 
                              t.toLowerCase().trim() !== lowerOption
                            ),
                          });
                        }
                      }}
                    >
                      {option.replace(/-/g, " ")}
                    </button>
                  );
                })}
              </div>
            </div>

              <div className="behavioral-form-field">
                <label className="behavioral-form-label">
                  {t("behavioralData.socialization")}
                </label>
                <div className="behavioral-pill-container">
                {validSocializations.map((option) => {
                  const isChecked = traitsFormData.socialization?.some(s => 
                    s.toLowerCase().trim() === option.toLowerCase().trim()
                  ) || false;
                  return (
                    <button
                      key={option}
                      type="button"
                      className={`behavioral-pill-btn ${isChecked ? 'active' : ''}`}
                      onClick={() => {
                        const current = traitsFormData.socialization || [];
                        const lowerOption = option.toLowerCase().trim();
                        if (!isChecked) {
                          setTraitsFormData({
                            ...traitsFormData,
                            socialization: [...current.filter(s => 
                              s.toLowerCase().trim() !== lowerOption
                            ), option],
                          });
                        } else {
                          setTraitsFormData({
                            ...traitsFormData,
                            socialization: current.filter((s) => 
                              s.toLowerCase().trim() !== lowerOption
                            ),
                          });
                        }
                      }}
                    >
                      {option.replace(/-/g, " ")}
                    </button>
                  );
                })}
              </div>
            </div>

              <div className="behavioral-form-actions">
                {(() => {
                  const hasChanged = traits.id ? (() => {
                    const currentTemp = (traitsFormData.temperament || []).map(t => String(t).toLowerCase().trim()).sort().join(',');
                    const originalTemp = (originalTraitsFormData.temperament || []).map(t => String(t).toLowerCase().trim()).sort().join(',');
                    const currentSoc = (traitsFormData.socialization || []).map(s => String(s).toLowerCase().trim()).sort().join(',');
                    const originalSoc = (originalTraitsFormData.socialization || []).map(s => String(s).toLowerCase().trim()).sort().join(',');
                    return currentTemp !== originalTemp || currentSoc !== originalSoc;
                  })() : true;

                  if (traits.id && !hasChanged) {
                    return (
                      <button
                        onClick={deleteTraits}
                        className="behavioral-form-btn-secondary"
                        style={{ borderColor: "#EF4444", color: "#EF4444" }}
                      >
                        Clear Traits
                      </button>
                    );
                  } else {
                    return (
                      <button
                        onClick={saveTraits}
                        className="behavioral-form-btn-primary"
                      >
                        Save
                      </button>
                    );
                  }
                })()}
                <button
                  onClick={() => setTraitsFormOpen(false)}
                  className="behavioral-form-btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="behavioral-traits-display">
            <div>
              <strong>{t("behavioralData.temperament")}:</strong>{" "}
              {traits.temperament || "-"}
            </div>
            <div>
              <strong>{t("behavioralData.socialization")}:</strong>{" "}
              {traits.socialization || "-"}
            </div>
          </div>
        )}
      </div>

      {/* Training Records */}
      <div className="behavioral-section">
        <div className="behavioral-section-header">
          <h2 className="behavioral-section-title">
            <img src={pawIcon} alt="icon" />
            {t("behavioralData.trainingRecords")}
          </h2>
          {!trainingFormOpen && (
            <button
              className="behavioral-action-btn"
              onClick={handleAddTraining}
            >
              {t("behavioralData.addTraining")}
            </button>
          )}
        </div>

        {trainingFormOpen ? (
          <div className="behavioral-form-card">
            <div className="behavioral-form-header">
              <h2>
                {trainingFormMode === "add"
                  ? t("behavioralData.addNewTrainingRecord")
                  : t("behavioralData.editTrainingRecord")}
              </h2>
            </div>
            <div className="behavioral-form-content">
              <div className="behavioral-form-field">
                <label className="behavioral-form-label">
                  {t("behavioralData.trainer")}
                </label>
                <input
                  type="text"
                  className="behavioral-form-input"
                  placeholder={t("behavioralData.enterTrainerName")}
                  value={trainingFormData.Trainer || ""}
                  onChange={(e) =>
                    setTrainingFormData({
                      ...trainingFormData,
                      Trainer: e.target.value,
                    })
                  }
                />
              </div>

              <div className="behavioral-form-field">
                <label className="behavioral-form-label">
                  {t("behavioralData.trainingType")}
                </label>
                <input
                  type="text"
                  className="behavioral-form-input"
                  placeholder={t("behavioralData.enterTrainingType")}
                  value={trainingFormData["Training Type"] || ""}
                  onChange={(e) =>
                    setTrainingFormData({
                      ...trainingFormData,
                      "Training Type": e.target.value,
                    })
                  }
                />
              </div>

              <div className="behavioral-form-field">
                <label className="behavioral-form-label">
                  {t("behavioralData.progress")}
                </label>
                <input
                  type="text"
                  className="behavioral-form-input"
                  placeholder="Enter progress status"
                  value={trainingFormData.Progress || ""}
                  onChange={(e) =>
                    setTrainingFormData({
                      ...trainingFormData,
                      Progress: e.target.value,
                    })
                  }
                />
              </div>

              <div className="behavioral-form-actions">
                <button
                  onClick={saveTraining}
                  className="behavioral-form-btn-primary"
                >
                  {trainingFormMode === "add" ? "Save" : "Update"}
                </button>
                <button
                  onClick={() => setTrainingFormOpen(false)}
                  className="behavioral-form-btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
      ) : (
        <>
          {/* Training Table */}
          <div className="behavioral-table-wrapper">
            <table className="behavioral-table">
              <thead>
              <tr>
                <th>{t("behavioralData.trainer")}</th>
                <th>{t("behavioralData.trainingType")}</th>
                <th>{t("behavioralData.progress")}</th>
                <th style={{ width: "110px", textAlign: "center" }}>
                  {t("behavioralData.actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {currentTraining.length === 0 ? (
                <tr>
                  <td colSpan={4} className="behavioral-empty-state">
                    No training records yet
                  </td>
                </tr>
              ) : (
                currentTraining.map((row, idx) => (
                  <tr key={idx}>
                    <td data-label={t("behavioralData.trainer")}>{row.Trainer}</td>
                    <td data-label={t("behavioralData.trainingType")}>{row["Training Type"]}</td>
                    <td data-label={t("behavioralData.progress")}>{row.Progress}</td>
                    <td style={{ textAlign: "center" }} data-label={t("behavioralData.actions")}>
                      <div className="behavioral-table-actions">
                        <button
                          className="behavioral-table-btn"
                          onClick={() =>
                            handleEditTraining(
                              row,
                              (trainingPage - 1) * rowsPerPage + idx
                            )
                          }
                        >
                          <img src={editIcon} alt="Edit" />
                        </button>
                        <button
                          className="behavioral-table-btn"
                          onClick={() =>
                            handleDelete(
                              "training",
                              (trainingPage - 1) * rowsPerPage + idx
                            )
                          }
                        >
                          <img src={deleteIcon} alt="Delete" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
        </>
      )}
      </div>

      {/* Behavioral Issues */}
      <div className="behavioral-section" style={{ marginTop: "40px" }}>
        <div className="behavioral-section-header">
          <h2 className="behavioral-section-title">
            <img src={pawIcon} alt="icon" />
            {t("behavioralData.behavioralIssues")}
          </h2>
          {!issuesFormOpen && (
            <button
              className="behavioral-action-btn"
              onClick={handleAddIssue}
            >
              {t("behavioralData.addIssue")}
            </button>
          )}
        </div>

        {issuesFormOpen ? (
          <div className="behavioral-form-card">
            <div className="behavioral-form-header">
              <h2>
                {issuesFormMode === "add"
                  ? "Add New Behavioral Issue"
                  : "Edit Behavioral Issue"}
              </h2>
            </div>
            <div className="behavioral-form-content">
              <div className="behavioral-form-field">
                <label className="behavioral-form-label">Issue</label>
                <input
                  type="text"
                  className="behavioral-form-input"
                  placeholder="Enter issue"
                  value={issuesFormData.Issue || ""}
                  onChange={(e) =>
                    setIssuesFormData({ ...issuesFormData, Issue: e.target.value })
                  }
                />
              </div>

              <div className="behavioral-form-field">
                <label className="behavioral-form-label">Severity</label>
                <select
                  className="behavioral-form-select"
                  value={issuesFormData.Severity || ""}
                  onChange={(e) =>
                    setIssuesFormData({
                      ...issuesFormData,
                      Severity: e.target.value,
                    })
                  }
                >
                  <option value="">Select severity</option>
                  <option value="mild">Mild</option>
                  <option value="moderate">Moderate</option>
                  <option value="severe">Severe</option>
                </select>
              </div>

              <div className="behavioral-form-field">
                <label className="behavioral-form-label">Management Plan</label>
                <input
                  type="text"
                  className="behavioral-form-input"
                  placeholder="Enter management plan"
                  value={issuesFormData["Management Plan"] || ""}
                  onChange={(e) =>
                    setIssuesFormData({
                      ...issuesFormData,
                      "Management Plan": e.target.value,
                    })
                  }
                />
              </div>

              <div className="behavioral-form-actions">
                <button
                  onClick={saveIssue}
                  className="behavioral-form-btn-primary"
                >
                  {issuesFormMode === "add" ? "Save" : "Update"}
                </button>
                <button
                  onClick={() => setIssuesFormOpen(false)}
                  className="behavioral-form-btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
      ) : (
        <>
          {/* Issues Table */}
          <div className="behavioral-table-wrapper">
            <table className="behavioral-table">
            <thead>
              <tr>
                <th>{t("behavioralData.issue")}</th>
                <th>{t("behavioralData.severity")}</th>
                <th>{t("behavioralData.managementPlan")}</th>
                <th style={{ width: "110px", textAlign: "center" }}>
                  {t("behavioralData.actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {currentIssues.length === 0 ? (
                <tr>
                  <td colSpan={4} className="behavioral-empty-state">
                    No behavioral issues yet
                  </td>
                </tr>
              ) : (
                currentIssues.map((row, idx) => (
                  <tr key={idx}>
                    <td data-label={t("behavioralData.issue")}>{row.Issue}</td>
                    <td data-label={t("behavioralData.severity")}>{row.Severity}</td>
                    <td data-label={t("behavioralData.managementPlan")}>{row["Management Plan"]}</td>
                    <td style={{ textAlign: "center" }} data-label={t("behavioralData.actions")}>
                      <div className="behavioral-table-actions">
                        <button
                          className="behavioral-table-btn"
                          onClick={() =>
                            handleEditIssue(
                              row,
                              (issuesPage - 1) * rowsPerPage + idx
                            )
                          }
                        >
                          <img src={editIcon} alt="Edit" />
                        </button>
                        <button
                          className="behavioral-table-btn"
                          onClick={() =>
                            handleDelete(
                              "issue",
                              (issuesPage - 1) * rowsPerPage + idx
                            )
                          }
                        >
                          <img src={deleteIcon} alt="Delete" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
        </>
      )}
      </div>

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        itemToDelete="Record"
      />
    </div>
  );
};

export default BehavioralDataView;
